import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { bombasColors } from 'Styles/colors';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import RichTextPropTypes from '../../prop-types/rich-text';
import CTAPropTypes from '../../prop-types/cta.pim';
import ImageProps from '../../prop-types/image-props.pim';
import { useBreakpoint } from '../../util/Breakpoint.context';

import * as Styled from './HeaderBlock.styled';
import { HeaderBlockFragment } from './HeaderBlock.gql';
import { CollectionPageContext } from '../CollectionPage/CollectionPage.context';

const opts = {
  renderText: (text) =>
    text
      .split('\n')
      .reduce(
        (children, textSegment, index) => [
          ...children,
          index > 0 && <br key={index} />,
          textSegment
        ],
        []
      )
};

/**
 * A full width component that can display text over an image or solid background color, with CTAs and links
 *
 * It contains an optional flag to render CTAs adaptively on mobile in an overflow container to replicate the `QuickLinks` functionality
 */
const HeaderBlock = ({
  backgroundDesktopImage,
  backgroundMobileImage,
  headerBlockHeader: header,
  content,
  textColor,
  ctas,
  backgroundColor,
  customBackgroundColor,
  headerBlockLayout,
  textAlignment,
  verticalAlignment
}) => {
  const { isCollection } = useContext(CollectionPageContext);
  /**
   * If the block does not have a desktop and mobile background image, and has a backgroundColor or a custom background color, the styled components will render the text with the most readable color
   */
  const hasImages = !!(
    backgroundDesktopImage?.file.url && backgroundMobileImage?.file.url
  );
  const imageDimensions = hasImages
    ? {
        desktop: {
          width: backgroundDesktopImage.file.details.image.width,
          height: backgroundDesktopImage.file.details.image.height
        },
        mobile: {
          width: backgroundMobileImage.file.details.image.width,
          height: backgroundMobileImage.file.details.image.height
        }
      }
    : {};

  const defaultBackgroundColor = isCollection ? 'grey40' : 'white';

  const color =
    customBackgroundColor ||
    convertColorToThemeHexCode(backgroundColor || defaultBackgroundColor);
  /**
   * If images are present, HeaderBlock is rendered in relative positioned wrapper
   */
  const Wrapper = hasImages ? Styled.HeaderBlockWrapper : React.Fragment;

  let CTAColor = color;

  if (textColor === 'LIGHT') {
    CTAColor = convertColorToThemeHexCode('black');
  }

  if (textColor === 'DARK') {
    CTAColor = convertColorToThemeHexCode('white');
  }

  const desktopProps = hasImages
    ? {
        alt: '',
        src: backgroundDesktopImage.file.url.match(/https:\/\/|http:\/\//)
          ? backgroundDesktopImage.file.url
          : `https://${backgroundDesktopImage.file.url}`,
        resolution: { medium: imageDimensions.desktop.width },
        crop: {
          height: imageDimensions.desktop.height,
          width: imageDimensions.desktop.width
        }
      }
    : {};

  const mobileProps = hasImages
    ? {
        alt: '',
        src: backgroundMobileImage.file.url.match(/https:\/\/|http:\/\//)
          ? backgroundMobileImage.file.url
          : `https://${backgroundMobileImage.file.url}`,
        resolution: { small: imageDimensions.mobile.width },
        crop: {
          height: imageDimensions.mobile.height,
          width: imageDimensions.mobile.width
        }
      }
    : {};

  const isGifFileType = new RegExp(/\.(gif)$/g);
  const { large } = useBreakpoint();

  return (
    <Wrapper>
      {hasImages && (
        <Styled.BackgroundImageBlock>
          {large &&
            (isGifFileType.test(backgroundDesktopImage.file.url) ? (
              <Styled.DesktopGif {...desktopProps} />
            ) : (
              <Styled.DesktopImage {...desktopProps} />
            ))}
          {!large &&
            (isGifFileType.test(backgroundMobileImage.file.url) ? (
              <Styled.MobileGif {...mobileProps} />
            ) : (
              <Styled.MobileImage {...mobileProps} />
            ))}
        </Styled.BackgroundImageBlock>
      )}
      <Styled.HeaderBlock
        $backgroundColor={color}
        $hasImages={hasImages}
        $layout={headerBlockLayout}
        $verticalAlignment={verticalAlignment}
        $textAlignment={textAlignment}
      >
        <Styled.Editorial
          $backgroundColor={color}
          $hasImages={hasImages}
          $textAlignment={textAlignment}
          $textColor={textColor}
          $verticalAlignment={verticalAlignment}
        >
          <Styled.Title $hasImages={hasImages} $layout={headerBlockLayout}>
            {header}
          </Styled.Title>
          {content && Object.keys(content).length && (
            <Styled.RichText $hasImages={hasImages} $layout={headerBlockLayout}>
              {documentToReactComponents(content, opts)}
            </Styled.RichText>
          )}
          {large && ctas?.length && (
            <Styled.CTABlock
              ctas={ctas}
              color={CTAColor}
              $textAlignment={textAlignment}
            />
          )}
        </Styled.Editorial>
        {!large && ctas?.length ? (
          <Styled.CTABlock
            ctas={ctas}
            color={CTAColor}
            withOverflow
            overflowOffset={
              textAlignment !== 'CENTER' ? 'calc(1 / 12 * 100%)' : ''
            }
            $textAlignment={textAlignment}
            $verticalAlignment={verticalAlignment}
          />
        ) : null}
      </Styled.HeaderBlock>
    </Wrapper>
  );
};

HeaderBlock.propTypes = {
  /**
   * An image src URL for desktop
   */
  backgroundDesktopImage: ImageProps,
  /**
   * An image src URL for mobile
   */
  backgroundMobileImage: ImageProps,
  /**
   * The header in the HeaderBlock
   */
  headerBlockHeader: PropTypes.string,
  /**
   * Supplemental copy for the header in a Contentful Rich Text AST
   */
  content: PropTypes.shape(RichTextPropTypes),

  textColor: PropTypes.oneOf(['LIGHT', 'DARK', 'AUTO']),
  /**
   * A list of links
   */
  ctas: PropTypes.arrayOf(PropTypes.shape(CTAPropTypes)),
  /**
   * The NAME of the color passed from the API
   *
   * @example white, grey20, bombasBlue
   *
   * The name passed from the API will be mapped to the color name to retrieve a hex code
   */
  backgroundColor: PropTypes.oneOf(bombasColors),
  /**
   * A prop for off-brand colors.
   *
   * If this field is present, it will override 'backgroundColor'
   */
  customBackgroundColor: PropTypes.string,
  /**
   * Reduces the top/bottom padding if compressed
   */
  headerBlockLayout: PropTypes.oneOf(['REGULAR', 'COMPRESSED']),
  /**
   * Left align or center the copy (horizontally)
   */
  textAlignment: PropTypes.oneOf(['LEFT', 'CENTER']),
  /**
   * Align the copy vertically
   */
  verticalAlignment: PropTypes.oneOf(['TOP', 'CENTER', 'BOTTOM'])
};

HeaderBlock.defaultProps = {
  backgroundDesktopImage: {
    description: ''
  },
  backgroundMobileImage: {
    description: ''
  },
  content: null,
  textColor: 'AUTO',
  ctas: [],
  customBackgroundColor: null,
  headerBlockLayout: 'COMPRESSED',
  textAlignment: 'CENTER',
  verticalAlignment: 'CENTER',
  headerBlockHeader: ''
};

HeaderBlock.fragment = HeaderBlockFragment;

export default HeaderBlock;
